'use client';

import type { FC, ReactNode, WheelEvent } from 'react';
import { DesktopHeader } from './DesktopHeader';
import { DesktopNav } from './DesktopNav';
import { DesktopDropsFeed } from './DesktopDropsFeed';
import { useDesktopContentWidth } from '@/contexts/DesktopContentWidthContext';
import { cn } from '@/utils/cn';

interface DesktopLayoutProps {
  children: ReactNode;
  minimal?: boolean;
}

export const DesktopLayout: FC<DesktopLayoutProps> = ({ children, minimal = false }) => {
  // Ширину колонки задаёт экран: по умолчанию 4xl, но экран может
  // потребовать всю ширину окна через <DesktopFullBleed /> — так открывается
  // горизонтальная рулетка кейсов.
  const contentWidth = useDesktopContentWidth();
  // Content is constrained to a 4xl-wide column. The wider viewport
  // leaves dead space on either side that isn't a scroll container,
  // so the wheel does nothing when the cursor sits there. Forward
  // wheel events from the empty side area into the screen's actual
  // scroll container (ScreenContent's div, marked with the
  // `data-screen-scroll` attribute).
  const handleSideWheel = (e: WheelEvent<HTMLElement>) => {
    const scroller = e.currentTarget.querySelector(
      '[data-screen-scroll]',
    ) as HTMLElement | null;
    if (!scroller) return;
    if (scroller.contains(e.target as Node)) return;
    scroller.scrollBy({ top: e.deltaY, left: e.deltaX, behavior: 'auto' });
  };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden bg-bg-primary pt-[calc(var(--tg-viewport-safe-area-inset-top,0px)+var(--tg-viewport-content-safe-area-inset-top,0px))]">
      {!minimal && <DesktopDropsFeed />}
      {!minimal && <DesktopHeader />}
      {!minimal && <DesktopNav />}
      <div className="flex flex-1 min-h-0">
        <main
          className="flex-1 min-w-0 relative outline-none focus:outline-none focus-visible:outline-none"
          onWheel={handleSideWheel}
        >
          <div
            className={cn(
              'w-full mx-auto h-full relative',
              contentWidth === 'column' && 'max-w-4xl'
            )}
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
